import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useScheduleByEventToken } from '../hooks/useSchedule';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const fmtDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const nextStatus = (cur) => {
  if (!cur) return 'working';
  if (cur === 'working') return 'absent';
  return null;
};

const STATUS_CONFIG = {
  working: { label: '出勤',   bg: '#ecfdf5', color: '#059669', border: '#6ee7b7', dot: '#10b981', icon: '✓' },
  absent:  { label: '欠勤',   bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', dot: '#ef4444', icon: '✕' },
  null:    { label: '未回答', bg: '#f8f9fd', color: '#94a3b8', border: '#e2e8f0', dot: '#cbd5e1', icon: '?' },
};

// レスポンシブ対応 CSS
const PageStyles = () => (
  <style>{`
    .sp-root {
      min-height: 100vh;
      background: linear-gradient(135deg, #f5f3ff 0%, #e0f2fe 100%);
      display: flex;
      justify-content: center;
      padding: 24px 16px 56px;
      box-sizing: border-box;
      font-family: 'Inter','Helvetica Neue','Hiragino Sans',sans-serif;
    }
    .sp-inner { width: 100%; max-width: 840px; }
    .sp-auth-wrap { max-width: 400px; margin: 0 auto; }
    .sp-card {
      background: #fff; border-radius: 18px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.10); padding: 24px;
    }
    .sp-months { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    .sp-day {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; aspect-ratio: 1; border-radius: 10px;
      border: 2px solid transparent; transition: all 0.15s; user-select: none; gap: 2px;
    }
    .sp-day-num  { font-size: 15px; line-height: 1; }
    .sp-day-lbl  { font-size: 10px; font-weight: 800; line-height: 1; }
    .sp-day-dot  { width: 5px; height: 5px; border-radius: 50%; display: inline-block; }
    @media (max-width: 640px) {
      .sp-root    { padding: 12px 10px 48px; }
      .sp-card    { padding: 18px 14px; border-radius: 14px; }
      .sp-months  { grid-template-columns: 1fr; gap: 20px; }
      .sp-day-num { font-size: 16px; }
      .sp-day-lbl { font-size: 10px; }
    }
  `}</style>
);

// ── カレンダーグリッド（キャスト向け）────────────────────────────────────────
const CastCalendar = ({ year, month, dates, castId, attendance, session_attendance, sessions, onToggle, onSelectDate, selectedDate }) => {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mm          = String(month + 1).padStart(2, '0');
  const todayStr    = fmtDate(new Date());

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // 部制あり: セルに何部出勤かを表示
  const getSessionSummary = (dateStr) => {
    if (!sessions) return null;
    const data = session_attendance?.[castId]?.[dateStr] || {};
    const values = Object.values(data);
    const working = values.filter(v => v === 'working').length;
    const absent  = values.filter(v => v === 'absent').length;
    const total = sessions;
    // 回答済み = 出勤 or 欠勤 がひとつでもある
    const answered = working > 0 || absent > 0;
    return { working, absent, total, answered };
  };

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 16, textAlign: 'center', marginBottom: 10, color: '#0f172a' }}>
        {year}年{month + 1}月
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
        {WEEKDAY_LABELS.map((w, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#94a3b8', padding: '3px 0' }}>{w}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e${idx}`} />;
          const dateStr = `${year}-${mm}-${String(day).padStart(2, '0')}`;
          const isEvent = !!dates[dateStr];
          const isToday = dateStr === todayStr;
          const isSel   = dateStr === selectedDate;
          const wd      = (firstDay + day - 1) % 7;

          let cfg = STATUS_CONFIG['null'];
          let badge = null;

          if (sessions) {
            const sum = getSessionSummary(dateStr);
            if (sum && sum.working > 0 && sum.working === sum.total) cfg = STATUS_CONFIG.working;
            else if (sum && sum.working > 0) cfg = { ...STATUS_CONFIG.working, bg: '#fffbeb', color: '#d97706', border: '#fde68a', dot: '#f59e0b' };
            else if (sum && sum.answered) cfg = STATUS_CONFIG.absent; // 回答済みだが出勤なし → 欠勤
            badge = sum && sum.working > 0 ? `${sum.working}/${sum.total}` : null;
          } else {
            const status = attendance[castId]?.[dateStr] ?? null;
            cfg = STATUS_CONFIG[status] || STATUS_CONFIG['null'];
          }

          return (
            <div
              key={dateStr}
              className="sp-day"
              onClick={() => {
                if (!isEvent) return;
                if (sessions) { onSelectDate(dateStr); }
                else { onToggle(dateStr, attendance[castId]?.[dateStr] ?? null); }
              }}
              style={{
                borderColor:  isEvent ? (isSel ? '#6c63ff' : cfg.border) : '#f1f5f9',
                background:   isEvent ? (isSel ? '#ede9fe' : cfg.bg) : '#fafafa',
                cursor:       isEvent ? 'pointer' : 'default',
                opacity:      isEvent ? 1 : 0.38,
                outline:      isToday && isEvent ? '2px solid #6c63ff66' : 'none',
                outlineOffset: 1,
              }}
            >
              <span className="sp-day-num" style={{
                fontWeight: isToday || isEvent ? 800 : 500,
                color: isEvent ? cfg.color : wd === 0 ? '#ef4444' : wd === 6 ? '#3b82f6' : '#94a3b8',
              }}>{day}</span>
              {isEvent && (
                <span className="sp-day-lbl" style={{ color: cfg.color, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {badge ? (
                    <span>{badge}</span>
                  ) : (
                    <>
                      <span style={{ fontSize: 10, lineHeight: 1, fontWeight: 900 }}>{cfg.icon}</span>
                      {cfg.label}
                    </>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── 部制出勤入力パネル ──────────────────────────────────────────────────────────
const SessionPanel = ({ dateStr, castId, sessions, session_attendance, cast_comments, setSessionAttendance, setAllSessionAttendance, setCastComment }) => {
  const d = new Date(...dateStr.split('-').map((v, i) => i === 1 ? Number(v) - 1 : Number(v)));
  const label = `${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAY_LABELS[d.getDay()]}）`;
  const [comment, setComment] = useState(cast_comments?.[castId]?.[dateStr] ?? '');
  const [saving, setSaving] = useState(false);

  const sesData = session_attendance?.[castId]?.[dateStr] || {};

  const STATUS_BTN = [
    { value: 'working', label: '✓ 出勤', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
    { value: 'absent',  label: '✕ 欠勤', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
    { value: null,      label: '? 未設定', color: '#94a3b8', bg: '#f8f9fd', border: '#e2e8f0' },
  ];

  const handleComment = async () => {
    setSaving(true);
    await setCastComment(castId, dateStr, comment.trim());
    setSaving(false);
  };

  return (
    <div style={{ marginTop: 20, background: '#fff', borderRadius: 16, padding: '18px 20px', border: '1px solid #e8edf5', boxShadow: '0 4px 16px rgba(0,0,0,0.07)' }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 14 }}>
        {label} の出勤設定
      </div>

      {/* 一括ボタン */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setAllSessionAttendance(castId, dateStr, sessions, 'working')}
          style={{ padding: '8px 16px', borderRadius: 10, border: '1.5px solid #6ee7b7', background: '#ecfdf5', color: '#059669', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          ✓ 全部出勤
        </button>
        <button onClick={() => setAllSessionAttendance(castId, dateStr, sessions, 'absent')}
          style={{ padding: '8px 16px', borderRadius: 10, border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          ✕ 全部欠勤
        </button>
        <button onClick={() => setAllSessionAttendance(castId, dateStr, sessions, null)}
          style={{ padding: '8px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8f9fd', color: '#94a3b8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          ? 全部未設定
        </button>
      </div>

      {/* 部ごとのトグル */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {Array.from({ length: sessions }, (_, i) => i + 1).map(ses => {
          const cur = sesData[ses] ?? null;
          return (
            <div key={ses} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#475569', minWidth: 44 }}>第{ses}部</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {STATUS_BTN.map(({ value, label, color, bg, border }) => {
                  const isActive = cur === value;
                  return (
                    <button key={String(value)} onClick={() => setSessionAttendance(castId, dateStr, ses, isActive ? null : value)}
                      style={{
                        padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        border: `1.5px solid ${isActive ? border : '#e2e8f0'}`,
                        background: isActive ? bg : '#fff',
                        color: isActive ? color : '#94a3b8',
                        transition: 'all 0.15s',
                      }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* コメント入力 */}
      <div>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
          📝 コメント（運営への連絡・任意）
        </label>
        <textarea
          rows={3}
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="例：参加できる予定ですが確定ではないです。。"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
          onBlur={handleComment}
        />
        {saving && <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>保存中...</span>}
      </div>
    </div>
  );
};

// ── 通常モード: 日付ごとのコメント入力 ───────────────────────────────────────
const DateComment = ({ dateStr, castId, cast_comments, setCastComment }) => {
  const [comment, setComment] = useState(cast_comments?.[castId]?.[dateStr] ?? '');

  return (
    <div style={{ marginTop: 6 }}>
      <textarea
        rows={2}
        value={comment}
        onChange={e => setComment(e.target.value)}
        onBlur={() => setCastComment(castId, dateStr, comment.trim())}
        placeholder="コメントを残す（任意）"
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, resize: 'none', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, color: '#475569' }}
      />
    </div>
  );
};

// ── VRChat名 入力＋選択コンボボックス ────────────────────────────────────────
const CastNamePicker = ({ casts, value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const allNames = Object.values(casts)
    .map(c => (c.vrchat_name || c.name || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'ja'));

  // 入力値で候補を絞り込み（空なら全件）
  const q = value.trim().toLowerCase();
  const filtered = q
    ? allNames.filter(n => n.toLowerCase().includes(q))
    : allNames;

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          style={{ ...s.input, paddingRight: 42, borderColor: error ? '#ef4444' : (open ? '#6c63ff' : '#e2e8f0') }}
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="VRChat名を入力または選択"
          autoFocus
        />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-label="リストから選択"
          style={{
            position: 'absolute', top: 0, right: 0, height: '100%', width: 40,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8',
          }}
        >
          <span style={{
            display: 'inline-block', transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none', fontSize: 12,
          }}>▼</span>
        </button>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto',
          padding: 4,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#94a3b8' }}>
              一致する名前がありません
            </div>
          ) : (
            filtered.map(name => {
              const active = name.toLowerCase() === q;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onChange(name); setOpen(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: active ? '#f5f3ff' : 'transparent',
                    color: active ? '#6c63ff' : '#334155',
                    fontSize: 14, fontWeight: active ? 800 : 600,
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  {name}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── メインページ ──────────────────────────────────────────────────────────────
const SchedulePage = () => {
  const { eventId, token } = useParams();
  const { eventName, schedule, casts, loading, error, setAttendance, setSessionAttendance, setAllSessionAttendance, setCastComment } = useScheduleByEventToken(eventId, token);

  const [step, setStep]           = useState('auth');
  const [inputName, setInputName] = useState('');
  const [authError, setAuthError] = useState('');
  const [castId, setCastId]       = useState(null);
  const [castName, setCastName]   = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef(null);

  const showToast = () => {
    setToastVisible(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastVisible(false), 2000);
  };

  const now = new Date();
  const months = [
    { year: now.getFullYear(), month: now.getMonth() },
    {
      year:  now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear(),
      month: (now.getMonth() + 1) % 12,
    },
  ];

  const sessions          = schedule.sessions ?? null;
  const session_attendance = schedule.session_attendance || {};
  const cast_comments     = schedule.cast_comments || {};

  const handleAuth = (e) => {
    e.preventDefault();
    const name = inputName.trim();
    if (!name) return;
    const found = Object.entries(casts).find(
      ([, c]) => (c.vrchat_name || c.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (!found) {
      setAuthError('このイベントに登録されていないVRChat名です。\nスタッフにお問い合わせください。');
      return;
    }
    setCastId(found[0]);
    setCastName(found[1].vrchat_name || found[1].name);
    setStep('calendar');
    setAuthError('');
  };

  const handleToggle = async (dateStr, currentStatus) => {
    await setAttendance(castId, dateStr, nextStatus(currentStatus));
    showToast();
  };

  const handleSetSessionAttendance = async (...args) => {
    await setSessionAttendance(...args);
    showToast();
  };

  const handleSetAllSessionAttendance = async (...args) => {
    await setAllSessionAttendance(...args);
    showToast();
  };

  const handleSetCastComment = async (...args) => {
    await setCastComment(...args);
    showToast();
  };

  const workingDays = sessions
    ? Object.keys(schedule.dates || {}).filter(d =>
        Object.values(session_attendance?.[castId]?.[d] || {}).some(s => s === 'working')
      ).length
    : Object.keys(schedule.dates || {}).filter(
        d => schedule.attendance?.[castId]?.[d] === 'working'
      ).length;

  if (loading) return (
    <>
      <PageStyles />
      <div className="sp-root" style={{ alignItems: 'center' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          <p>読み込み中...</p>
        </div>
      </div>
    </>
  );

  if (error) return (
    <>
      <PageStyles />
      <div className="sp-root" style={{ alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ color: '#1e293b', margin: '0 0 8px' }}>無効なリンクです</h2>
          <p style={{ color: '#94a3b8', fontSize: 14 }}>URLが正しいか確認してください。</p>
        </div>
      </div>
    </>
  );

  // 通常モード: コメント欄を表示するイベント日付一覧
  const eventDates = Object.keys(schedule.dates || {}).sort();

  return (
    <>
      <PageStyles />
      <div className="sp-root">
        <div className="sp-inner">

          {/* ── 認証ステップ ── */}
          {step === 'auth' && (
            <div className="sp-auth-wrap">
              <div className="sp-card">
                <Header eventName={eventName} />
                <p style={s.desc}>
                  登録されているVRChat名を入力、またはリストから選択してください。<br />
                  カレンダーから開催日ごとの出欠を設定できます。
                </p>
                <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <CastNamePicker
                    casts={casts}
                    value={inputName}
                    onChange={(v) => { setInputName(v); setAuthError(''); }}
                    error={authError}
                  />
                  {authError && (
                    <div style={s.errorBox}>
                      {authError.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={!inputName.trim()}
                    style={{ ...s.primaryBtn, opacity: inputName.trim() ? 1 : 0.5, cursor: inputName.trim() ? 'pointer' : 'not-allowed' }}
                  >
                    確認する →
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* ── カレンダーステップ ── */}
          {step === 'calendar' && (
            <div className="sp-card">
              <Header eventName={eventName} />

              {/* ウェルカムバー */}
              <div style={s.welcomeBar}>
                <div style={s.castAvatar}>{castName.charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.castName}>{castName}</div>
                  <div style={s.castSub}>
                    出勤予定: {workingDays}日
                    {sessions && <span style={{ marginLeft: 8 }}>（第{sessions}部制）</span>}
                  </div>
                </div>
                <button onClick={() => setStep('auth')} style={s.changeBtn}>変更</button>
              </div>

              {/* 凡例 */}
              {!sessions && (
                <div style={s.legend}>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 900, color: cfg.color, flexShrink: 0 }}>{cfg.icon}</span>
                      {cfg.label}
                    </span>
                  ))}
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 2 }}>（タップで切替）</span>
                </div>
              )}
              {sessions && (
                <div style={{ ...s.legend, justifyContent: 'flex-start' }}>
                  <span style={{ fontSize: 12, color: '#6c63ff', fontWeight: 700 }}>第{sessions}部制</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>開催日をタップして部ごとに設定</span>
                </div>
              )}

              {/* カレンダー */}
              <div className="sp-months">
                {months.map(({ year, month }) => (
                  <CastCalendar
                    key={`${year}-${month}`}
                    year={year}
                    month={month}
                    dates={schedule.dates || {}}
                    castId={castId}
                    attendance={schedule.attendance || {}}
                    session_attendance={session_attendance}
                    sessions={sessions}
                    onToggle={handleToggle}
                    onSelectDate={setSelectedDate}
                    selectedDate={selectedDate}
                  />
                ))}
              </div>

              {/* 部制ありの場合: 選択日の入力パネル */}
              {sessions && selectedDate && (
                <SessionPanel
                  dateStr={selectedDate}
                  castId={castId}
                  sessions={sessions}
                  session_attendance={session_attendance}
                  cast_comments={cast_comments}
                  setSessionAttendance={handleSetSessionAttendance}
                  setAllSessionAttendance={handleSetAllSessionAttendance}
                  setCastComment={handleSetCastComment}
                />
              )}

              {/* 部制なしの場合: 開催日ごとのコメント欄 */}
              {!sessions && eventDates.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 10, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                    各開催日へのコメント（任意）
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {eventDates.map(dateStr => {
                      const d = new Date(...dateStr.split('-').map((v, i) => i === 1 ? Number(v) - 1 : Number(v)));
                      const label = `${d.getMonth() + 1}/${d.getDate()}（${WEEKDAY_LABELS[d.getDay()]}）`;
                      const status = schedule.attendance?.[castId]?.[dateStr];
                      const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['null'];
                      return (
                        <div key={dateStr} style={{ background: cfg.bg, borderRadius: 10, padding: '10px 14px', border: `1px solid ${cfg.border}` }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: cfg.color, marginBottom: 4 }}>
                            {label} <span style={{ fontSize: 11, fontWeight: 600 }}>（{cfg.label}）</span>
                          </div>
                          <DateComment
                            dateStr={dateStr}
                            castId={castId}
                            cast_comments={cast_comments}
                            setCastComment={handleSetCastComment}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={s.note}>
                ※ 色付きの日付のみ入力できます　／　変更はリアルタイムで保存されます
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 自動保存トースト */}
      <div style={{
        position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
        background: '#1e293b', color: '#fff',
        padding: '10px 20px', borderRadius: 24,
        fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 7,
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        opacity: toastVisible ? 1 : 0,
        transition: 'opacity 0.25s',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 9999,
      }}>
        <span style={{ color: '#4ade80', fontSize: 15 }}>✓</span> 保存しました
      </div>
    </>
  );
};

const Header = ({ eventName }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #f1f5f9',
  }}>
    <img src="/logo.png" alt="LinkBoard" style={{ height: 34, width: 'auto', flexShrink: 0 }} />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.5 }}>イベント出欠入力</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {eventName || 'イベント'}
      </div>
    </div>
  </div>
);

const s = {
  desc: { fontSize: 14, color: '#64748b', lineHeight: 1.75, margin: '0 0 20px' },
  input: {
    padding: '13px 16px', borderRadius: 12, border: '1.5px solid #e2e8f0',
    fontSize: 15, outline: 'none', width: '100%', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  errorBox: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#dc2626', lineHeight: 1.6,
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, #6c63ff, #8b5cf6)', color: '#fff', border: 'none',
    borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, width: '100%',
    boxShadow: '0 4px 14px rgba(108,99,255,0.38)', transition: 'opacity 0.15s',
  },
  welcomeBar: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: '#f5f3ff', border: '1px solid #ddd6fe',
    borderRadius: 12, padding: '10px 14px', marginBottom: 14,
  },
  castAvatar: {
    width: 36, height: 36, borderRadius: 10,
    background: '#6c63ff', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 800, flexShrink: 0,
  },
  castName: { fontSize: 14, fontWeight: 800, color: '#1e293b' },
  castSub:  { fontSize: 11, color: '#6c63ff', fontWeight: 600 },
  changeBtn: {
    flexShrink: 0, background: 'transparent', border: '1px solid #ddd6fe',
    borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600,
    color: '#6c63ff', cursor: 'pointer',
  },
  legend: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    marginBottom: 18, padding: '10px 12px',
    background: '#fafbff', borderRadius: 10, border: '1px solid #f1f5f9',
  },
  note: { marginTop: 20, fontSize: 12, color: '#94a3b8', lineHeight: 1.7, textAlign: 'center' },
};

export default SchedulePage;
